import {
  Component,
  OnDestroy,
  OnInit,
  ElementRef,
  ViewChild
} from '@angular/core';

import {
  ActivatedRoute,
  Router
} from '@angular/router';


import * as ExcelJS from 'exceljs/dist/exceljs.min.js';
import { finalize } from 'rxjs/operators';

import { AdminService } from 'src/app/services/admin/admin.service';
import { CourseService } from 'src/app/services/course/course.service';
import { FileUploadService } from 'src/app/services/file-upload/file-upload.service';
import Quill from 'quill';
import { XCourseDetails } from 'src/app/core/XCourseDetails';
import { StateModel } from 'src/app/core/StateModel';
import { CourseDownloadModel } from 'src/app/core/CourseDownloadModel';
import { XCourseFilter } from 'src/app/core/common/XCourseFilter';

const SizeStyle: any = Quill.import('attributors/style/size');

SizeStyle.whitelist = [
  '10px',
  '12px',
  '14px',
  '16px',
  '18px',
  '20px',
  '22px',
  '24px',
  '28px',
  '32px'
];

Quill.register(SizeStyle, true);



@Component({
  selector: 'app-admin-short-courses',
  templateUrl: './admin-short-courses.component.html',
  styleUrl: './admin-short-courses.component.scss'
})
export class AdminShortCoursesComponent implements OnInit, OnDestroy {

  // ============================================================
  // COURSE LIST
  // ============================================================

  courseType: string = 'ALL';

  // Filter the admin list by the course visibility status.
  visibilityFilter: string = 'ALL';

  allUOCCourses: any[] = [];

  // Search by course name or code.
  searchText: string = '';

  isLoading: boolean = false;


  // ============================================================
  // COURSE DETAILS
  // ============================================================

  CourseDetails: XCourseDetails | null = null;

  isDetailsLoading: boolean = false;

  showEditModal: boolean = false;

  isSaving: boolean = false;

  // Course currently being deleted from the admin list.
  deletingCourseId: number | null = null;

  // Proper confirmation popup state.
  showDeleteModal: boolean = false;

  // Course waiting for confirmation.
  pendingDeleteCourse: any | null = null;

  // True when the shared popup is being used to manually create a course.
  isCreateMode: boolean = false;


  // ============================================================
  // FILE UPLOAD
  // ============================================================

  isUploadingImage: boolean = false;

  isUploadingBrochure: boolean = false;

  imagePreviewUrl: string = '';

  selectedBrochureName: string = '';


  // ============================================================
  // HERO IMAGE SIZE EDITOR
  // ============================================================

  readonly imageSizeModes: Array<{
    value: 'HEIGHT' | 'WIDTH' | 'CUSTOM'; label: string; icon: string;
  }> = [
      { value: 'HEIGHT', label: 'Auto width', icon: 'bi-arrows-vertical' },
      { value: 'WIDTH', label: 'Auto height', icon: 'bi-arrows' },
      { value: 'CUSTOM', label: 'Custom Size', icon: 'bi-aspect-ratio' }
    ];

  readonly imageAlignmentOptions: Array<{
    value: 'LEFT' | 'CENTER' | 'RIGHT'; label: string; icon: string;
  }> = [
      { value: 'LEFT', label: 'Left', icon: 'bi-text-left' },
      { value: 'CENTER', label: 'Center', icon: 'bi-text-center' },
      { value: 'RIGHT', label: 'Right', icon: 'bi-text-right' }
    ];

  private normalizeImageAlignment(value: unknown): 'LEFT' | 'CENTER' | 'RIGHT' {
    const alignment = String(value ?? 'CENTER').toUpperCase();
    return alignment === 'LEFT' || alignment === 'RIGHT' ? alignment : 'CENTER';
  }

  changeImageAlignment(value: 'LEFT' | 'CENTER' | 'RIGHT'): void {
    if (!this.CourseDetails) { return; }
    this.CourseDetails.imageAlignment = value;
  }

  // Simulated desktop public image area; change PREVIEW_BASE_WIDTH if your desktop
  // image column is substantially different. Public layout remains responsive.
  readonly PREVIEW_BASE_WIDTH = 700;
  readonly PREVIEW_BASE_HEIGHT = 500;

  private previewScale = 420 / 700;
  private naturalImageWidth = 700;
  private naturalImageHeight = 500;
  private previewResizeObserver?: ResizeObserver;

  @ViewChild('heroSizingPreview')
  set heroSizingPreview(element: ElementRef<HTMLElement> | undefined) {
    this.previewResizeObserver?.disconnect();
    if (!element) { return; }

    const el = element.nativeElement;
    const update = () => {
      this.previewScale = el.getBoundingClientRect().width / this.PREVIEW_BASE_WIDTH;
    };
    update();
    this.previewResizeObserver = new ResizeObserver(update);
    this.previewResizeObserver.observe(el);
  }

  private heroResizeSession: {
    pointerId: number;
    startX: number;
    startY: number;
    width: number;
    height: number;
    scale: number;
    mode: 'HEIGHT' | 'WIDTH' | 'CUSTOM';
  } | null = null;

  private normalizeImageSizeMode(raw: unknown): 'HEIGHT' | 'WIDTH' | 'CUSTOM' | null {
    const value = String(raw ?? '').toUpperCase();
    return value === 'HEIGHT' || value === 'WIDTH' || value === 'CUSTOM'
      ? value : null;
  }

  changeImageSizeMode(mode: 'HEIGHT' | 'WIDTH' | 'CUSTOM'): void {
    if (!this.CourseDetails) { return; }
    this.CourseDetails.imageSizeMode = mode;
    this.CourseDetails.imageWidth = this.CourseDetails.imageWidth ?? 700;
    this.CourseDetails.imageHeight = this.CourseDetails.imageHeight ?? 500;
  }

  normalizeImageDimension(which: 'width' | 'height'): void {
    if (!this.CourseDetails) { return; }
    const property = which === 'width' ? 'imageWidth' : 'imageHeight';
    const numeric = Number(this.CourseDetails[property]);
    // Do not overwrite a temporarily blank input while typing; validate on Save.
    if (Number.isFinite(numeric) && numeric >= 40) {
      this.CourseDetails[property] = Math.round(numeric);
    }
  }

  onHeroPreviewImageLoad(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img.naturalWidth && img.naturalHeight) {
      this.naturalImageWidth = img.naturalWidth;
      this.naturalImageHeight = img.naturalHeight;
    }
  }


  private get targetPreviewImageSize(): {
    width: number;
    height: number;
  } {

    const c = this.CourseDetails;

    const mode = c?.imageSizeMode;

    const ratio =
      this.naturalImageWidth /
      Math.max(1, this.naturalImageHeight);

    const width = Number(c?.imageWidth ?? 700);

    const height = Number(c?.imageHeight ?? 500);

    if (mode === 'HEIGHT') {
      return {
        width: height * ratio,
        height
      };
    }

    if (mode === 'WIDTH') {
      return {
        width,
        height: width / ratio
      };
    }

    if (mode === 'CUSTOM') {
      return {
        width,
        height
      };
    }

    return {
      width: this.PREVIEW_BASE_WIDTH,
      height: this.PREVIEW_BASE_HEIGHT
    };
  }

  get previewImageWidth(): number {
    return this.targetPreviewImageSize.width * this.previewScale;
  }

  get previewImageHeight(): number {
    return this.targetPreviewImageSize.height * this.previewScale;
  }


  /* ============================================================
    Start Hero Image Resize
 ============================================================ */

  startHeroImageResize(event: PointerEvent): void {

    if (!this.CourseDetails?.imageSizeMode) {
      return;
    }

    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    const button = event.currentTarget as HTMLElement;

    const preview = button.closest(
      '.ecb-image-sizing-preview'
    ) as HTMLElement | null;

    if (!preview) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.heroResizeSession = {
      pointerId: event.pointerId,

      startX: event.clientX,
      startY: event.clientY,

      width: Number(this.CourseDetails.imageWidth ?? 700),
      height: Number(this.CourseDetails.imageHeight ?? 500),

      scale: Math.max(0.01, this.previewScale),

      mode: this.CourseDetails.imageSizeMode
    };

    // Capture movements on the preview, not the small button
    preview.setPointerCapture(event.pointerId);
  }


  /* ============================================================
     Move Hero Image Resize
  ============================================================ */


  moveHeroImageResize(event: PointerEvent): void {

    const session = this.heroResizeSession;

    if (
      !session ||
      session.pointerId !== event.pointerId ||
      !this.CourseDetails
    ) {
      return;
    }

    event.preventDefault();

    // Top-right corner resize direction
    const dx =
      (event.clientX - session.startX) / session.scale;

    const dy =
      (session.startY - event.clientY) / session.scale;

    // Update Width
    if (
      session.mode === 'WIDTH' ||
      session.mode === 'CUSTOM'
    ) {
      this.CourseDetails.imageWidth = Math.max(
        40,
        Math.min(
          1600,
          Math.round(session.width + dx)
        )
      );
    }

    // Update Height
    if (
      session.mode === 'HEIGHT' ||
      session.mode === 'CUSTOM'
    ) {
      this.CourseDetails.imageHeight = Math.max(
        40,
        Math.min(
          1200,
          Math.round(session.height + dy)
        )
      );
    }
  }



  /* ============================================================
    Stop Hero Image Resize
 ============================================================ */

  stopHeroImageResize(event: PointerEvent): void {

    const session = this.heroResizeSession;

    if (
      !session ||
      session.pointerId !== event.pointerId
    ) {
      return;
    }

    this.heroResizeSession = null;

    const preview = event.currentTarget as HTMLElement;

    if (preview.hasPointerCapture(event.pointerId)) {
      preview.releasePointerCapture(event.pointerId);
    }
  }
  private validHeroImageSize(): boolean {
    const c = this.CourseDetails;
    if (!c?.imageSizeMode) { return true; } // Preserve legacy cover mode.
    const width = Number(c.imageWidth);
    const height = Number(c.imageHeight);
    const widthValid = Number.isInteger(width) && width >= 40 && width <= 1600;
    const heightValid = Number.isInteger(height) && height >= 40 && height <= 1200;
    return c.imageSizeMode === 'WIDTH' ? widthValid
      : c.imageSizeMode === 'HEIGHT' ? heightValid
        : widthValid && heightValid;
  }


  // ============================================================
  // MESSAGES
  // ============================================================

  successMessage: string = '';

  errorMessage: string = '';


  // ============================================================
  // DELETE SHORT COURSE
  // ============================================================

  requestDeleteCourse(course: any): void {

    const courseId =
      this.getCourseId(course);

    if (
      !courseId ||
      this.deletingCourseId !== null
    ) {
      return;
    }

    this.resetMessages();

    this.pendingDeleteCourse = course;
    this.showDeleteModal = true;

    document.body.style.overflow = 'hidden';
  }


  closeDeleteModal(): void {

    if (this.deletingCourseId !== null) {
      return;
    }

    this.showDeleteModal = false;
    this.pendingDeleteCourse = null;

    document.body.style.overflow = '';
  }


  confirmDeleteCourse(): void {

    if (!this.pendingDeleteCourse) {
      return;
    }

    const courseId =
      this.getCourseId(
        this.pendingDeleteCourse
      );

    if (
      !courseId ||
      this.deletingCourseId !== null
    ) {
      return;
    }

    this.resetMessages();

    this.deletingCourseId = courseId;

    this.adminService
      .DeleteXCourse(courseId)
      .pipe(
        finalize(() => {

          this.deletingCourseId = null;

        })
      )
      .subscribe({

        next: () => {

          this.successMessage =
            'Short course deleted successfully.';

          this.allUOCCourses =
            this.allUOCCourses.filter(item =>
              this.getCourseId(item) !== courseId
            );

          this.ensureValidPage();

          this.showDeleteModal = false;
          this.pendingDeleteCourse = null;

          document.body.style.overflow = '';

          // Reload to keep the table, VETtrak status and pagination
          // fully aligned with the database after deletion.
          this.loadUOCCourses();
        },

        error: (error) => {

          console.error(
            'Delete short course error:',
            error
          );

          this.errorMessage =
            error?.error?.message ??
            error?.error?.Message ??
            (
              typeof error?.error === 'string'
                ? error.error
                : null
            ) ??
            'Unable to delete the short course.';
        }
      });
  }




  // ============================================================
  // RICH TEXT EDITOR CONFIG
  // ============================================================


  editorModules = {
    toolbar: [
      [
        {
          size: [
            '10px',
            '12px',
            '14px',
            '16px',
            '18px',
            '20px',
            '22px',
            '24px',
            '28px',
            '32px'
          ]
        }
      ],

      ['bold', 'italic', 'underline', 'strike'],

      [
        { header: 1 },
        { header: 2 }
      ],

      [
        { list: 'ordered' },
        { list: 'bullet' }
      ],

      [
        { indent: '-1' },
        { indent: '+1' }
      ],

      [
        { align: [] }
      ],

      // Keep existing Quill colour palette
      [
        { color: [] },
        { background: [] },

        // Custom HEX colour button
        'hexColor'
      ],

      // [
      //   { color: [] },
      //   { background: [] }
      // ],

      ['link'],

      ['clean']
    ],
    handlers: {
      hexColor: function (this: any) {

        const quill = this.quill;

        const currentColor = prompt(
          'Enter text colour HEX value.\nExample: #E31E24'
        );

        if (!currentColor) {
          return;
        }

        let color = currentColor.trim();

        // Automatically add #
        if (!color.startsWith('#')) {
          color = '#' + color;
        }

        // Validate HEX
        const isValidHex =
          /^#([0-9A-F]{3}){1,2}$/i.test(color);

        if (!isValidHex) {
          alert(
            'Invalid HEX colour.\nPlease enter something like #E31E24'
          );
          return;
        }

        quill.format('color', color);
      }
    }
  };

  //store states list 
  statesList: StateModel[] = [];

  //store selected state code
  selectedStateCode: string = 'MEL';

  //store form state code
  selectedFormStateCode: string = 'MEL';

  filter: XCourseFilter = {
    action: '',
    type: '',
    search: '',
    searchKey: '',
    isActive: null,
    stateCode: '',
    pageSize: 10,
    currentPage: 1
  };

  currentPage: number = 1;
  pageSize: number = 10;
  totalRecords: number = 0;

  pageSizeOptions: number[] = [10, 20, 50, 100];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private adminService: AdminService,
    private uploadService: FileUploadService,
    private courseService: CourseService,
  ) {
  }


  // ============================================================
  // INIT
  // ============================================================

  ngOnInit(): void {

    this.route.queryParamMap.subscribe(params => {

      this.courseType =
        (params.get('type') ?? 'ALL').toUpperCase();

      this.currentPage = 1;

      this.loadUOCCourses();
    });

    this.loadStates();
  }


  ngOnDestroy(): void {

    document.body.style.overflow = '';
    this.previewResizeObserver?.disconnect();

    if (
      this.imagePreviewUrl &&
      this.imagePreviewUrl.startsWith('blob:')
    ) {

      URL.revokeObjectURL(
        this.imagePreviewUrl
      );
    }
  }

  loadStates(): void {

    this.adminService.getStatesService('ALL_ACTIVE').subscribe({
      next: (response: StateModel[]) => {
        this.statesList = response || [];
      },
      error: error => {
        console.error('Failed to load states:', error);
        this.statesList = [];
      }
    });

  }

  onStateChange(stateCode: string): void {

    this.selectedStateCode = stateCode;
    this.selectedFormStateCode = stateCode;
    const selectedState = this.statesList.find(
      x => x.code === stateCode
    );
    this.onCourseTypeChange();

  }


  onFormStateChange(stateCode: string): void {

    this.selectedFormStateCode = stateCode;

    const selectedState = this.statesList.find(
      x => x.code === stateCode
    );


  }



  // ============================================================
  // LOAD COURSES
  // ============================================================
  loadUOCCourses(): void {

    this.filter.action = 'GET_BY_TYPE_ADMIN';
    this.filter.type = this.courseType;
    this.filter.search = this.searchText || '';
    this.filter.searchKey = '';
    this.filter.isActive = this.getVisibilityFilterValue();
    this.filter.stateCode = this.selectedStateCode;
    this.filter.pageSize = this.pageSize;
    this.filter.currentPage = this.currentPage;

    this.isLoading = true;

    this.adminService
      .GetXCourseOptimizedListByType(this.filter)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({

        next: (response) => {

          this.allUOCCourses = response.records || [];

          this.totalRecords = response.totalRecords || 0;

          this.currentPage = response.currentPage;

          this.ensureValidPage();
        },

        error: (error) => {

          console.error('Course load error:', error);

          this.allUOCCourses = [];

          this.totalRecords = 0;
        }
      });
  }

  loadSkillSetsCourses(): void {

    this.filter.action = 'GET_SKILL_BY_TYPE_ADMIN';
    this.filter.type = this.courseType;
    this.filter.search = this.searchText || '';
    this.filter.searchKey = '';
    this.filter.isActive = this.getVisibilityFilterValue();
    this.filter.stateCode = this.selectedStateCode;
    this.filter.pageSize = this.pageSize;
    this.filter.currentPage = this.currentPage;

    this.isLoading = true;

    this.adminService
      .GetXCourseOptimizedListByType(this.filter)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({

        next: (response) => {

          this.allUOCCourses = response.records || [];

          this.totalRecords = response.totalRecords || 0;

          this.currentPage = response.currentPage;

          this.ensureValidPage();
        },

        error: (error) => {

          console.error('Course load error:', error);

          this.allUOCCourses = [];

          this.totalRecords = 0;
        }
      });
  }

  // ============================================================
  // SERVER-SIDE PAGINATION
  // ============================================================

  // Courses already filtered and paginated by SQL
  get filteredCourses(): any[] {
    return this.allUOCCourses;
  }


  // No client-side slicing required
  get paginatedCourses(): any[] {
    return this.allUOCCourses;
  }


  // Total pages based on total SQL records
  get totalPages(): number {

    return Math.max(
      1,
      Math.ceil(this.totalRecords / this.pageSize)
    );
  }


  // First record number of current page
  get pageStart(): number {

    if (this.totalRecords === 0) {
      return 0;
    }

    return ((this.currentPage - 1) * this.pageSize) + 1;
  }


  // Last record number of current page
  get pageEnd(): number {

    return Math.min(
      this.currentPage * this.pageSize,
      this.totalRecords
    );
  }


  // Pagination page numbers
  get pageNumbers(): number[] {

    const maxButtons = 5;

    let startPage = Math.max(
      1,
      this.currentPage - 2
    );

    let endPage = Math.min(
      this.totalPages,
      startPage + maxButtons - 1
    );

    startPage = Math.max(
      1,
      endPage - maxButtons + 1
    );

    return Array.from(
      {
        length: endPage - startPage + 1
      },
      (_, index) => startPage + index
    );
  }


  // Change page
  changePage(page: number): void {

    if (
      page < 1 ||
      page > this.totalPages ||
      page === this.currentPage ||
      this.isLoading
    ) {
      return;
    }

    this.currentPage = page;

    this.reloadCurrentCourseList();
  }


  // Change page size
  changePageSize(value: string | number): void {

    const newPageSize = Number(value);

    if (!this.pageSizeOptions.includes(newPageSize)) {
      return;
    }

    this.pageSize = newPageSize;
    this.currentPage = 1;

    this.reloadCurrentCourseList();
  }


  // Search change
  onSearchChange(): void {

    this.currentPage = 1;

    this.reloadCurrentCourseList();
  }


  // Reload correct course list
  private reloadCurrentCourseList(): void {

    if (this.filter.action === 'GET_SKILL_BY_TYPE_ADMIN') {

      this.loadSkillSetsCourses();

    } else {

      this.loadUOCCourses();

    }
  }


  // Validate current page
  private ensureValidPage(): void {

    if (this.currentPage > this.totalPages) {

      this.currentPage = this.totalPages;

      this.reloadCurrentCourseList();

      return;
    }

    if (this.currentPage < 1) {

      this.currentPage = 1;
    }
  }

  // ============================================================
  // OPEN NEW MANUAL COURSE
  // ============================================================

  openNewCourse(): void {

    this.resetMessages();
    this.isCreateMode = true;
    this.isDetailsLoading = false;
    this.imagePreviewUrl = '';
    this.naturalImageWidth = 700;
    this.naturalImageHeight = 500;
    this.selectedBrochureName = '';

    this.CourseDetails = {
      id: 0,
      courseCode: '',
      courseTitle: '',
      totalAmount: 0,
      currencyCode: 'AUD',
      documentTypes: '',
      description: '',
      imageUrl: '',
      imageSizeMode: 'HEIGHT',
      imageAlignment: 'CENTER',
      imageWidth: 700,
      imageHeight: 500,
      brochureUrl: '',
      nCourseFor: '',
      nCovered: '',
      nPreconditions: '',
      nKnowledgeAssessment: '',
      nSkillsAssessment: '',
      nCertificateValid: '',
      nHowLongCourse: '',
      nShortDescription: '',
      isActive: true,
      isCourseAvailableInCalendar: false,
      isSyncActive: false,
      adminCourseType: this.courseType || 'UOC',
      wasSyncedFromVettrak: false,
      lastVettrakSyncUtc: null,
      syncSourceUrl: null,
      syncStatus: null,
      syncProgramId: null,
      occurances: [],
      units: [],
      classes: [],
      isHighRiskCourse: false,
      isSkillSetsCourse: false,
      isHsrCourse: false
    };

    this.showEditModal = true;
    document.body.style.overflow = 'hidden';
  }


  // ============================================================
  // OPEN COURSE DETAILS
  // ============================================================

  openCourseDetails(course: any): void {

    const courseId =
      Number(
        course?.id ??
        course?.Id ??
        0
      );

    if (!courseId) {
      return;
    }


    this.resetMessages();

    this.isCreateMode = false;
    this.CourseDetails = null;

    this.imagePreviewUrl = '';
    this.naturalImageWidth = 700;
    this.naturalImageHeight = 500;

    this.selectedBrochureName = '';

    this.isDetailsLoading = true;


    /*
     * Show modal immediately with loader.
     * Details appear after API response.
     */
    this.showEditModal = true;

    document.body.style.overflow = 'hidden';


    this.adminService
      .GetXCourseFullDetails(courseId)
      .pipe(
        finalize(() => {

          this.isDetailsLoading = false;

        })
      )
      .subscribe({

        next: (response: any) => {

          /*
           * Supports both:
           *
           * Object response
           * AND
           * [Object] response
           */
          const data =
            Array.isArray(response)
              ? response[0]
              : response;


          if (!data) {

            this.errorMessage =
              'Course details could not be found.';

            return;
          }


          /*
           * Normalise old SQL-style property names
           * and normal C# JSON property names.
           */
          this.CourseDetails = {

            id:
              data.id ??
              data.Id ??
              courseId,

            courseCode:
              data.courseCode ??
              data.CourseCode ??
              '',

            courseTitle:
              data.courseTitle ??
              data.CourseTitle ??
              '',

            totalAmount:
              Number(
                data.totalAmount ??
                data.TotalAmount ??
                0
              ),

            currencyCode:
              data.currencyCode ??
              data.CurrencyCode ??
              'AUD',

            documentTypes:
              data.documentTypes ??
              data.DocumentTypes ??
              '',

            description:
              data.description ??
              data.Description ??
              '',

            imageUrl:
              data.imageUrl ??
              data.ImageUrl ??
              '',

            imageSizeMode: this.normalizeImageSizeMode(
              data.imageSizeMode ?? data.ImageSizeMode
            ),
            imageAlignment: this.normalizeImageAlignment(
              data.imageAlignment ?? data.ImageAlignment
            ),
            imageWidth: data.imageWidth == null && data.ImageWidth == null
              ? null : Number(data.imageWidth ?? data.ImageWidth),
            imageHeight: data.imageHeight == null && data.ImageHeight == null
              ? null : Number(data.imageHeight ?? data.ImageHeight),

            brochureUrl:
              data.brochureUrl ??
              data.BrochureUrl ??
              '',


            nCourseFor:
              data.nCourseFor ??
              data.NCourseFor ??
              data.n_course_for ??
              '',


            nCovered:
              data.nCovered ??
              data.NCovered ??
              data.n_covered ??
              '',


            nPreconditions:
              data.nPreconditions ??
              data.NPreconditions ??
              data.n_preconditions ??
              '',


            nKnowledgeAssessment:
              data.nKnowledgeAssessment ??
              data.NKnowledgeAssessment ??
              data.n_knowledge_assessment ??
              '',


            nSkillsAssessment:
              data.nSkillsAssessment ??
              data.NSkillsAssessment ??
              data.n_skills_Assessment ??
              '',


            nCertificateValid:
              data.nCertificateValid ??
              data.NCertificateValid ??
              data.n_certificate_valid ??
              '',

            nHowLongCourse:
              data.nHowLongCourse ??
              data.nHowLongCourse ??
              data.nHowLongCourse ??
              '',

            nShortDescription:
              data.nShortDescription ??
              data.nShortDescription ??
              data.nShortDescription ??
              '',


            isActive:
              data.isActive ??
              data.IsActive ??
              false,


            isCourseAvailableInCalendar:
              data.isCourseAvailableInCalendar ??
              false,
            //turn off always
            /* isSyncActive:
              data.isSyncActive ??
              data.IsSyncActive ??
              false, */
            isSyncActive: false,

            adminCourseType:
              data.adminCourseType ??
              data.AdminCourseType ??
              this.courseType ??
              'UOC',

            wasSyncedFromVettrak:
              data.wasSyncedFromVettrak ??
              data.WasSyncedFromVettrak ??
              false,

            lastVettrakSyncUtc:
              data.lastVettrakSyncUtc ??
              data.LastVettrakSyncUtc ??
              null,

            syncSourceUrl:
              data.syncSourceUrl ??
              data.SyncSourceUrl ??
              null,

            syncStatus:
              data.syncStatus ??
              data.SyncStatus ??
              null,

            syncProgramId:
              data.syncProgramId ??
              data.SyncProgramId ??
              null,

            occurances:
              data.occurances ??
              data.Occurances ??
              [],

            units:
              data.units ??
              data.Units ??
              [],

            classes:
              data.classes ??
              data.Classes ??
              [],

            isHighRiskCourse: data.isHighRiskCourse ??
              data.isHighRiskCourse ??
              false,

            isSkillSetsCourse:
              data.isSkillSetsCourse ??
              data.IsSkillSetsCourse ??
              false,

            stateCode: data.stateCode ??
              data.stateCode ??
              false,

            isHsrCourse: data.isHsrCourse ??
              data.isHsrCourse ??
              false
          };


          this.imagePreviewUrl =
            this.CourseDetails.imageUrl;

           
          this.selectedFormStateCode = this.CourseDetails.stateCode;
        },


        error: (error) => {

          console.error(
            'Course details error:',
            error
          );

          this.errorMessage =
            'Unable to load course details.';
        }
      });
  }


  // ============================================================
  // CLOSE MODAL
  // ============================================================

  closeCourseModal(): void {

    if (
      this.isSaving ||
      this.isUploadingImage ||
      this.isUploadingBrochure
    ) {
      return;
    }


    this.showEditModal = false;

    this.isCreateMode = false;
    this.CourseDetails = null;

    this.resetMessages();

    document.body.style.overflow = '';
  }


  // ============================================================
  // IMAGE UPLOAD
  // ============================================================

  // ============================================================
  // COMMON COURSE IMAGE UPLOAD
  // ============================================================


  // Handle successfully uploaded or removed image
  onCourseImageChange(imageUrl: string): void {

    if (!this.CourseDetails) {
      return;
    }

    // Update database model
    this.CourseDetails.imageUrl = imageUrl;

    // Update image preview
    this.imagePreviewUrl = imageUrl;

    // Reset natural image dimensions when image changes
    // The image load event will set actual dimensions
    this.naturalImageWidth = 700;
    this.naturalImageHeight = 500;

    // Clear previous messages
    this.resetMessages();

    // Show success only when an image exists
    if (imageUrl) {

      this.successMessage =
        'Course image uploaded successfully.';

    } else {

      this.successMessage =
        'Course image removed. Save changes to apply.';
    }
  }


  // Handle uploading state from common component
  onCourseImageUploadingChange(isUploading: boolean): void {

    this.isUploadingImage = isUploading;

  }


  // Handle upload errors
  onCourseImageUploadError(message: string): void {

    this.errorMessage =
      message || 'Unable to upload the course image.';

    console.error(
      'Course image upload error:',
      message
    );
  }


  // ============================================================
  // BROCHURE UPLOAD
  // ============================================================

  onBrochureSelected(event: Event): void {

    if (!this.CourseDetails) {
      return;
    }


    const input =
      event.target as HTMLInputElement;


    const file =
      input.files?.[0];


    if (!file) {
      return;
    }


    if (file.type !== 'application/pdf') {

      this.errorMessage =
        'Please select a PDF brochure.';

      input.value = '';

      return;
    }


    // 10 MB
    const maximumSize =
      10 * 1024 * 1024;


    if (file.size > maximumSize) {

      this.errorMessage =
        'Brochure must be smaller than 10 MB.';

      input.value = '';

      return;
    }


    this.resetMessages();


    this.selectedBrochureName =
      file.name;


    this.isUploadingBrochure = true;


    this.uploadService
      .uploadSingleFile(
        file,
        'course-brochure'

      )
      .pipe(
        finalize(() => {

          this.isUploadingBrochure = false;

          input.value = '';

        })
      )
      .subscribe({

        next: (response: any) => {

          const fileUrl = response?.publicUrl;

          if (!fileUrl) {

            this.errorMessage =
              'Brochure uploaded but no file URL was returned.';

            return;
          }


          if (!this.CourseDetails) {
            return;
          }


          this.CourseDetails.brochureUrl =
            fileUrl;


          this.successMessage =
            'Course brochure uploaded successfully.';
        },


        error: (error) => {

          console.error(
            'Brochure upload error:',
            error
          );


          this.selectedBrochureName = '';


          this.errorMessage =
            'Unable to upload the course brochure.';
        }
      });
  }


  // ============================================================
  // SAVE COURSE DETAILS
  // ============================================================

  updateCourseDetails(): void {


    if (!this.CourseDetails) {
      return;
    }

    this.resetMessages();

    if (!this.CourseDetails.courseCode?.trim()) {
      this.errorMessage = 'Course code is required.';
      return;
    }

    if (!this.CourseDetails.courseTitle?.trim()) {
      this.errorMessage = 'Course title is required.';
      return;
    }

    if (Number(this.CourseDetails.totalAmount ?? 0) < 0) {
      this.errorMessage = 'Course price cannot be negative.';
      return;
    }

    if (this.isUploadingImage || this.isUploadingBrochure) {
      this.errorMessage = 'Please allow the current file upload to finish.';
      return;
    }

    if (!this.validHeroImageSize()) {
      this.errorMessage = 'Enter valid image dimensions (width: 40–1600 px, height: 40–1200 px).';
      return;
    }


    const model = {
      id: this.CourseDetails.id,
      courseCode: this.CourseDetails.courseCode.trim(),
      courseTitle: this.CourseDetails.courseTitle.trim(),
      totalAmount: Number(this.CourseDetails.totalAmount ?? 0),
      currencyCode: (this.CourseDetails.currencyCode || 'AUD').trim().toUpperCase(),
      documentTypes: (this.CourseDetails as any).documentTypes ?? '',
      isActive: this.CourseDetails.isActive ?? true,
      description: this.CourseDetails.description ?? '',
      imageUrl: this.CourseDetails.imageUrl ?? '',

      imageSizeMode: this.CourseDetails.imageSizeMode ?? null,
      imageAlignment: this.normalizeImageAlignment(this.CourseDetails.imageAlignment),
      imageWidth: (this.CourseDetails.imageSizeMode === 'WIDTH' ||
        this.CourseDetails.imageSizeMode === 'CUSTOM')
        ? Number(this.CourseDetails.imageWidth) : null,
      imageHeight: (this.CourseDetails.imageSizeMode === 'HEIGHT' ||
        this.CourseDetails.imageSizeMode === 'CUSTOM')
        ? Number(this.CourseDetails.imageHeight) : null,
      brochureUrl: this.CourseDetails.brochureUrl ?? '',
      nCourseFor: this.CourseDetails.nCourseFor ?? '',
      nCovered: this.CourseDetails.nCovered ?? '',
      nPreconditions: this.CourseDetails.nPreconditions ?? '',
      nKnowledgeAssessment: this.CourseDetails.nKnowledgeAssessment ?? '',
      nSkillsAssessment: this.CourseDetails.nSkillsAssessment ?? '',
      nCertificateValid: this.CourseDetails.nCertificateValid ?? '',
      nHowLongCourse: this.CourseDetails.nHowLongCourse ?? '',
      nShortDescription: this.CourseDetails.nShortDescription ?? '',
      isSyncActive: this.CourseDetails.isSyncActive ?? false,

      adminCourseType:
        (this.CourseDetails.adminCourseType || this.courseType) === 'ALL'
          ? 'UOC'
          : (this.CourseDetails.adminCourseType || this.courseType || 'UOC'),
      isHighRiskCourse: this.CourseDetails.isHighRiskCourse ?? true,
      isSkillSetsCourse: this.CourseDetails.isSkillSetsCourse ?? false,
      stateCode: this.selectedFormStateCode ?? 'MEL',
      isHsrCourse: this.CourseDetails.isHsrCourse ?? false
    };



    this.isSaving = true;

    const request$ = this.isCreateMode
      ? this.adminService.CreateXCourse(model)
      : this.adminService.UpdateXCourseDetails(model);

    request$
      .pipe(finalize(() => {
        this.isSaving = false;
      }))
      .subscribe({
        next: (response: any) => {
          const created = this.isCreateMode;

          this.successMessage = created
            ? 'Short course created successfully.'
            : 'Course details updated successfully.';

          if (!created) {
            const existingCourse = this.allUOCCourses.find(
              x => Number(x.id ?? x.Id) === this.CourseDetails?.id
            );

            if (existingCourse && this.CourseDetails) {
              existingCourse.courseCode = this.CourseDetails.courseCode;
              existingCourse.courseTitle = this.CourseDetails.courseTitle;
              existingCourse.totalAmount = this.CourseDetails.totalAmount;
              existingCourse.currencyCode = this.CourseDetails.currencyCode;
              existingCourse.description = this.CourseDetails.description;
              existingCourse.imageUrl = this.CourseDetails.imageUrl;
              existingCourse.isActive = this.CourseDetails.isActive;
              existingCourse.isHighRiskCourse = this.CourseDetails.isHighRiskCourse;
              existingCourse.isSkillSetsCourse = this.CourseDetails.isSkillSetsCourse;
              existingCourse.stateCode = this.selectedFormStateCode;
              existingCourse.isHsrCourse = this.CourseDetails.isHsrCourse;
            }
          }

          // Reload so a newly created course immediately appears in the
          // correct UOC /  HRC admin list and receives its generated Id.
          if (this.courseType === 'SS') { this.loadSkillSetsCourses(); }
          else {
            this.loadUOCCourses();
          }

          setTimeout(() => {
            this.showEditModal = false;
            this.isCreateMode = false;
            this.CourseDetails = null;
            document.body.style.overflow = '';
          }, 500);
        },
        error: (error) => {
          console.error(
            this.isCreateMode ? 'Create course error:' : 'Update course error:',
            error
          );

          this.errorMessage =
            error?.error?.message ??
            error?.error?.Message ??
            (this.isCreateMode
              ? 'Unable to create the short course.'
              : 'Unable to update course details.');
        }
      });
  }


  // ============================================================
  // REMOVE IMAGE
  // ============================================================

  removeCourseImage(): void {

    this.onCourseImageChange('');
  }



  // ============================================================
  // DISPLAY HELPERS
  // ============================================================

  hasCourseImage(course: any): boolean {

    return !!(
      course?.imageUrl &&
      !course?.imageError
    );
  }


  onImageError(course: any): void {

    course.imageError = true;
  }


  getCourseId(course: any): number {

    return Number(
      course?.id ??
      course?.Id ??
      0
    );
  }


  getCourseCode(course: any): string {

    return (
      course?.courseCode ??
      course?.CourseCode ??
      'ECB COURSE'
    );
  }


  getCourseTitle(course: any): string {

    return (
      course?.courseTitle ??
      course?.CourseTitle ??
      'Untitled Course'
    );
  }


  getTableCourseTitle(course: any): string {

    const title =
      course?.courseTitle ??
      course?.CourseTitle ??
      'Untitled Course';

    const maxLength = 65;

    return title.length > maxLength
      ? title.substring(0, maxLength) + '...'
      : title;
  }


  getCourseDescription(course: any): string {

    const description =
      course?.description ??
      course?.Description ??
      'Course information is available in the course details.';


    return this.stripHtml(
      description
    );
  }


  getPrice(course: any): string {

    const amount =
      Number(
        course?.totalAmount ??
        course?.TotalAmount ??
        0
      );


    const currency =
      course?.currencyCode ??
      course?.CurrencyCode ??
      'AUD';


    if (amount <= 0) {

      return 'Enquire for price';
    }


    return `${currency} ${amount.toFixed(2)}`;
  }


  // ============================================================
  // HELPERS
  // ============================================================

  private stripHtml(value: string): string {

    if (!value) {
      return '';
    }


    const temporary =
      document.createElement('div');


    temporary.innerHTML =
      value;


    return (
      temporary.textContent ??
      temporary.innerText ??
      ''
    );
  }


  private getUploadedFileUrl(
    response: any
  ): string {

    return (
      response?.fileUrl ??
      response?.url ??
      response?.path ??
      response?.data?.fileUrl ??
      response?.data?.url ??
      ''
    );
  }


  private resetMessages(): void {

    this.successMessage = '';

    this.errorMessage = '';
  }



  private getVisibilityFilterValue(): boolean | null {

    if (this.visibilityFilter === 'ACTIVE') {
      return true;
    }

    if (this.visibilityFilter === 'INACTIVE') {
      return false;
    }

    return null;
  }


  onVisibilityFilterChange(): void {

    this.currentPage = 1;

    this.loadUOCCourses();
  }


  onCourseTypeChange(): void {

    this.currentPage = 1;

    if (this.courseType === 'SS') { this.loadSkillSetsCourses(); }
    else {
      this.loadUOCCourses();
    }
  }

  isRefreshing: boolean = false;




  private formatDate(date: Date): string {

    const year = date.getFullYear();

    const month = String(
      date.getMonth() + 1
    ).padStart(2, '0');

    const day = String(
      date.getDate()
    ).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  //remove brochure 
  removeBrochure() {
    this.CourseDetails.brochureUrl = '';
  }



  // false = Skill Sets
  isShortCourse: boolean = true;
  courseTypeOptions = [
    {
      label: 'Short Courses',
      value: false
    },
    {
      label: 'Skill Sets',
      value: true
    }
  ];


  onCourseTypeSelect(): void {

    if (!this.CourseDetails) return;

    if (this.CourseDetails.isSkillSetsCourse) {
      this.CourseDetails.isHighRiskCourse = false;
      this.CourseDetails.isHsrCourse = false;
    }

  }


  onHighRiskChange(value: boolean): void {
    if (!this.CourseDetails) return;

    if (this.CourseDetails.isSkillSetsCourse) {
      this.CourseDetails.isHighRiskCourse = false;
      return;
    }

    this.CourseDetails.isHighRiskCourse = value;

    if (value) {
      this.CourseDetails.isHsrCourse = false;
    }
  }

  onHsrChange(value: boolean): void {
    if (!this.CourseDetails) return;

    if (this.CourseDetails.isSkillSetsCourse) {
      this.CourseDetails.isHsrCourse = false;
      return;
    }

    this.CourseDetails.isHsrCourse = value;

    if (value) {
      this.CourseDetails.isHighRiskCourse = false;
    }
  }


  /* ============================================
     COURSE DOWNLOAD VARIABLES
  ============================================ */

  showDownloadDialog: boolean = false;

  downloadCourseType: 'ALL' | 'SHORT' | 'LONG' = 'ALL';

  downloadShortDescription: boolean = true;

  downloadDescription: boolean = true;

  isDownloading: boolean = false;


  /* ============================================
     OPEN DOWNLOAD POPUP
  ============================================ */

  openDownloadDialog(): void {

    this.downloadCourseType = 'ALL';

    this.downloadShortDescription = true;

    this.downloadDescription = true;

    this.showDownloadDialog = true;
  }


  /* ============================================
     DOWNLOAD COURSES AS EXCEL
  ============================================ */

  downloadCourseExcel(): void {

    if (this.isDownloading) {
      return;
    }

    this.isDownloading = true;

    this.adminService.getCourseDownload(
      this.downloadCourseType,
      this.downloadShortDescription,
      this.downloadDescription
    )
      .pipe(
        finalize(() => {
          this.isDownloading = false;
        })
      )
      .subscribe({

        next: async (courses: CourseDownloadModel[]) => {

          try {

            if (!courses || courses.length === 0) {
              alert('No courses found to download.');
              return;
            }

            await this.generateCourseExcel(courses);

            this.showDownloadDialog = false;

          } catch (error) {

            console.error('Excel generation failed:', error);

            alert('Unable to generate Excel file.');
          }

        },

        error: (error) => {

          console.error('Course download failed:', error);

          alert('Unable to retrieve courses.');

        }

      });

  }


  /* ============================================
     GENERATE EXCEL FILE
  ============================================ */


  private async generateCourseExcel(
    courses: CourseDownloadModel[]
  ): Promise<void> {

    // Create workbook
    const workbook = new ExcelJS.Workbook();

    workbook.creator = 'ECB Training';
    workbook.created = new Date();

    /* ============================================
       CREATE WORKSHEET
    ============================================ */

    const createWorksheet = (
      sheetName: string,
      courseList: CourseDownloadModel[]
    ): void => {

      const worksheet = workbook.addWorksheet(sheetName);

      /* ============================================
         DYNAMIC COLUMNS
      ============================================ */

      const columns: any[] = [

        {
          header: 'Course Code',
          key: 'courseCode',
          width: 22
        },

        {
          header: 'Course Title',
          key: 'courseTitle',
          width: 45
        }

      ];

      // Include short description
      if (this.downloadShortDescription) {

        columns.push({
          header: 'Short Description',
          key: 'shortDescription',
          width: 55
        });

      }

      // Include full description
      if (this.downloadDescription) {

        columns.push({
          header: 'Full Description',
          key: 'description',
          width: 70
        });

      }

      // Price column
      columns.push({
        header: 'Price',
        key: 'price',
        width: 18
      });

      // Course type column
      columns.push({
        header: 'Course Type',
        key: 'courseType',
        width: 18
      });

      // Course category column
      columns.push({
        header: 'Course Category',
        key: 'courseCategory',
        width: 22
      });

      worksheet.columns = columns;

      /* ============================================
         ADD COURSE DATA
      ============================================ */

      courseList.forEach((course) => {

        const rowData: any = {

          courseCode: course.courseCode || '',

          courseTitle: course.courseTitle || '',

          price: course.price ?? 0,

          courseType: course.courseType === 'LONG'
            ? 'Qualification'
            : 'Short Course',

          courseCategory: course.courseCategory || (
            course.courseType === 'LONG'
              ? 'QUALIFICATION'
              : 'UOC'
          )

        };
        if (this.downloadShortDescription) {

          rowData.shortDescription =
            this.getPlainExcelText(course.shortDescription);

        }

        if (this.downloadDescription) {

          rowData.description =
            this.getPlainExcelText(course.description);

        }

        worksheet.addRow(rowData);

      });

      /* ============================================
         HEADER STYLE - DARK ORANGE
      ============================================ */

      const headerRow = worksheet.getRow(1);

      headerRow.height = 30;

      headerRow.eachCell((cell) => {

        cell.font = {
          bold: true,
          color: { argb: 'FFFFFFFF' },
          size: 11
        };

        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFF8C00' }
        };

        cell.alignment = {
          vertical: 'middle',
          horizontal: 'center'
        };

        cell.border = {
          bottom: {
            style: 'thin',
            color: { argb: 'FFE67600' }
          }
        };

      });

      /* ============================================
         DATA ROW STYLING
      ============================================ */

      worksheet.eachRow((row, rowNumber) => {

        if (rowNumber === 1) {
          return;
        }

        row.eachCell((cell) => {

          cell.alignment = {
            vertical: 'top',
            wrapText: true
          };

          // Alternate row background
          if (rowNumber % 2 === 0) {

            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFFFF5E8' }
            };

          }

        });

      });

      /* ============================================
         PRICE FORMAT
      ============================================ */

      worksheet.getColumn('price').numFmt = '#,##0.00';

      /* ============================================
         FILTERS
      ============================================ */

      worksheet.autoFilter = {
        from: {
          row: 1,
          column: 1
        },
        to: {
          row: 1,
          column: columns.length
        }
      };

      /* ============================================
         FREEZE HEADER
      ============================================ */

      worksheet.views = [
        {
          state: 'frozen',
          ySplit: 1
        }
      ];

    };

    /* ============================================
       SEPARATE SHORT & LONG COURSES
    ============================================ */

    const shortCourses = courses.filter(
      course => course.courseType?.toUpperCase() === 'SHORT'
    );

    const qualifications = courses.filter(
      course => course.courseType?.toUpperCase() === 'LONG'
    );

    /* ============================================
       CREATE SHEETS BASED ON ACTION
    ============================================ */

    if (this.downloadCourseType === 'ALL') {

      // Sheet 1: Short Courses
      createWorksheet('Short Courses', shortCourses);

      // Sheet 2: Qualifications
      createWorksheet('Qualifications', qualifications);

    }
    else if (this.downloadCourseType === 'SHORT') {

      createWorksheet('Short Courses', shortCourses);

    }
    else if (this.downloadCourseType === 'LONG') {

      createWorksheet('Qualifications', qualifications);

    }

    /* ============================================
       GENERATE EXCEL FILE
    ============================================ */

    const buffer = await workbook.xlsx.writeBuffer();

    const blob = new Blob(
      [buffer],
      {
        type:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    );

    // File name
    const typeName = this.downloadCourseType === 'LONG'
      ? 'Qualifications'
      : this.downloadCourseType === 'SHORT'
        ? 'Short_Courses'
        : 'All_Courses';

    const fileName =
      `ECB_${typeName}_${this.getExcelDate()}.xlsx`;

    // Download file
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);

    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 1000);

  }


  /* ============================================
     CONVERT HTML DESCRIPTION TO PLAIN TEXT
  ============================================ */

  private getPlainExcelText(
    html: string | null | undefined
  ): string {

    if (!html) {
      return '';
    }

    const doc = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    doc.querySelectorAll('script, style').forEach(
      element => element.remove()
    );

    return doc.body.textContent?.trim() || '';
  }


  /* ============================================
     EXCEL FILE DATE
  ============================================ */

  private getExcelDate(): string {

    const date = new Date();

    const year = date.getFullYear();

    const month = String(
      date.getMonth() + 1
    ).padStart(2, '0');

    const day = String(
      date.getDate()
    ).padStart(2, '0');

    return `${year}${month}${day}`;

  }


}
